import {useState,useEffect,useCallback} from 'react';
import {supabase} from '@/services/supabaseClient';
import {listCloudUsers,manageCloudUser} from '@/services/cloudUsers';
import {responsibleNames,accessLevel} from '@/auth/access';
import type {User} from '@/db';
import {zsbAuthEmail} from '@/services/zsbIdentity';
const SIMULATION='zsb_simulated_user_id';
type State={user:User|null;originalUser:User|null;isImpersonating:boolean;isLoading:boolean};
const empty={user:null,originalUser:null,isImpersonating:false};
const notify=()=>window.dispatchEvent(new Event('zsb-auth-change'));
async function resolve(){
 const {data,error}=await supabase!.auth.getUser();
 if(error||!data.user)return empty;
 const rows=await listCloudUsers();
 const actual=rows.find(u=>u.authId===data.user.id);
 if(!actual?.active)return empty;
 let effective=actual;
 let isImpersonating=false;
 const id=sessionStorage.getItem(SIMULATION);
 if(id&&accessLevel(actual)==='admin'&&!actual.forcePasswordChange){
  const target=rows.find(u=>u.id===Number(id)&&u.active&&accessLevel(u)!=='admin');
  if(target){effective={...target,forcePasswordChange:false};isImpersonating=true;}else sessionStorage.removeItem(SIMULATION);
 }else if(id)sessionStorage.removeItem(SIMULATION);
 const team=accessLevel(effective)==='manager'?rows.filter(u=>u.managerEmail?.toLowerCase()===effective.email.toLowerCase()&&accessLevel(u)==='seller'):[];
 return {user:{...effective,teamEmails:team.map(u=>u.email.toLowerCase()),teamResponsibleNames:team.flatMap(responsibleNames),teamProductCodes:[...(effective.productCodes||[]),...team.flatMap(u=>u.productCodes||[])]},originalUser:isImpersonating?actual:null,isImpersonating};
}
export function useCloudAuth(){
 const [state,setState]=useState<State>({...empty,isLoading:true});
 useEffect(()=>{
  let alive=true,version=0;
  const refresh=()=>{const current=++version;resolve().then(value=>{if(alive&&current===version)setState({...value,isLoading:false});}).catch(()=>{if(alive&&current===version)setState({...empty,isLoading:false});});};
  refresh();
  const {data:{subscription}}=supabase!.auth.onAuthStateChange(()=>{queueMicrotask(refresh);});
  window.addEventListener('zsb-auth-change',refresh);
  return ()=>{alive=false;subscription.unsubscribe();window.removeEventListener('zsb-auth-change',refresh);};
 },[]);
 const login=useCallback(async(email:string,password:string):Promise<string|null>=>{
  sessionStorage.removeItem(SIMULATION);
  localStorage.removeItem('zsb_auth_user');
  const authEmail=await zsbAuthEmail(email);
  const {error}=await supabase!.auth.signInWithPassword({email:authEmail,password});
  if(error)return 'E-mail ou senha inválidos.';
  try {if(!(await resolve()).user){await supabase!.auth.signOut({scope:'local'});return 'Esta conta não tem acesso ativo ao ZSB.';}}catch{await supabase!.auth.signOut({scope:'local'});return 'Não foi possível validar as permissões ZSB.';}
  notify();return null;
 },[]);
 const logout=useCallback(async()=>{sessionStorage.removeItem(SIMULATION);localStorage.removeItem('zsb_auth_user');await supabase!.auth.signOut({scope:'local'});notify();},[]);
 const accessAs=useCallback(async(id:number):Promise<string|null>=>{
  const current=await resolve();
  if(!current.user||current.isImpersonating||accessLevel(current.user)!=='admin'||current.user.forcePasswordChange)return 'Somente administradores podem simular acesso.';
  const rows=await listCloudUsers();
  if(!rows.some(u=>u.id===id&&u.active&&accessLevel(u)!=='admin'))return 'Selecione um usuário ativo.';
  sessionStorage.setItem(SIMULATION,String(id));notify();return null;
 },[]);
 const stopAccessAs=useCallback(()=>{sessionStorage.removeItem(SIMULATION);notify();},[]);
 const changePassword=useCallback(async(password:string):Promise<string|null>=>{try{await manageCloudUser('change-password',{password});return null;}catch(e){return (e as Error).message;}},[]);
 const changePasswordWithCurrent=useCallback(async(currentPassword:string,password:string):Promise<string|null>=>{try{await manageCloudUser('change-password',{password,currentPassword});return null;}catch(e){return (e as Error).message;}},[]);
 return {...state,login,logout,accessAs,stopAccessAs,changePassword,changePasswordWithCurrent};
}
